import { IonButton, IonIcon, IonSpinner } from '@ionic/react'
import { checkmarkOutline, cloudUploadOutline } from 'ionicons/icons'
import { useRef, useState } from 'react'

import { uploadPaymentProof } from '../../lib/api'
import { canPickImage, outcome, pickImage } from '../../lib/native'

import type { DockProps } from './StepDock'

/*
 * The business's QR, and the customer's screenshot.
 *
 * Reached only AFTER the booking is committed, so the slot is already theirs
 * and nothing here can lose it. That is what makes every route out of this dock
 * safe: paying, skipping, or closing the app all leave a real appointment
 * behind.
 *
 * Nothing about money is integrated. The customer pays through their own UPI
 * app and uploads a screenshot; staff confirm each one against their own bank
 * app from the dashboard. That is the product decision, not a stopgap here.
 *
 * The upload does not travel through the step endpoint — a step payload is JSON
 * and an image is not — so it posts to its own endpoint, which returns the same
 * StepView and lets the whole dock re-render from server state rather than from
 * a local "did it work" flag.
 */

interface PaymentInfo {
  amountInr: number
  qrUrl: string | null
  upiId: string | null
  note: string | null
  proofUploaded: boolean
}

export function PaymentDock({ view, busy, onSend, onReplace, onNotice }: DockProps) {
  const info = view.result?.payment as PaymentInfo | undefined
  const [uploading, setUploading] = useState(false)
  const file = useRef<HTMLInputElement>(null)

  const uploaded = Boolean(info?.proofUploaded)
  const amount = info?.amountInr ?? 0

  async function upload(blob: Blob, filename: string) {
    setUploading(true)
    const result = await uploadPaymentProof(view.sessionId, blob, filename)
    setUploading(false)
    if (file.current) file.current.value = ''

    if (result.ok) {
      outcome('success')
      // The endpoint answers with a full StepView, so the parent swaps state and
      // this re-renders with proofUploaded true.
      onReplace(result.view)
    } else {
      outcome('warning')
      onNotice(result.message)
    }
  }

  /*
   * Two ways in, and the split is not cosmetic.
   *
   * On a device, Capacitor opens the OS gallery, with the permission dialog and
   * the look the customer already knows. In a browser there is no such picker,
   * and a file input is not a poor substitute for one — it IS the platform's
   * picker there.
   */
  async function choose() {
    if (canPickImage()) {
      const picked = await pickImage()
      if (picked) await upload(picked, 'proof.jpg')
      return
    }
    file.current?.click()
  }

  return (
    <>
      <div className="blooq-pay">
        <div className="blooq-pay__amt">₹{amount.toLocaleString('en-IN')}</div>

        {info?.qrUrl ? <img src={info.qrUrl} alt="Scan to pay" className="blooq-pay__qr" /> : null}
        {info?.upiId ? <div className="blooq-pay__upi">{info.upiId}</div> : null}
        {info?.note ? <p className="blooq-meta">{info.note}</p> : null}
      </div>

      {uploaded ? (
        <div className="blooq-pay__done">
          <IonIcon icon={checkmarkOutline} />
          Screenshot received — we will confirm it.
        </div>
      ) : null}

      <input
        ref={file}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={(event) => {
          const picked = event.target.files?.[0]
          if (picked) void upload(picked, picked.name)
        }}
      />

      <IonButton
        expand="block"
        fill="outline"
        disabled={busy || uploading}
        onClick={() => void choose()}
      >
        {uploading ? (
          <>
            <IonSpinner name="dots" style={{ marginInlineEnd: 8 }} />
            Uploading…
          </>
        ) : (
          <>
            <IonIcon slot="start" icon={cloudUploadOutline} />
            {uploaded ? 'Replace the screenshot' : 'Upload payment screenshot'}
          </>
        )}
      </IonButton>

      <IonButton
        expand="block"
        disabled={busy || uploading}
        onClick={() => onSend({ value: 'done' }, uploaded ? 'Paid' : 'Paid — will send proof later')}
      >
        <IonIcon slot="start" icon={checkmarkOutline} />
        {uploaded ? 'Done' : 'I’ve paid'}
      </IonButton>

      {/* The escape hatch. Without it a customer who cannot scan is stuck on
          this screen with a booking already in their name. */}
      <IonButton
        expand="block"
        fill="clear"
        disabled={busy || uploading}
        onClick={() => onSend({ value: 'later' }, 'Will pay in person')}
      >
        Pay in person instead
      </IonButton>
    </>
  )
}
